import { cn } from '@helpers/cn';
import type { TProps } from './types/TProps';

const RADIUS = 22;
const STROKE = 5;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
const SIZE = (RADIUS + STROKE) * 2;

export const QualityCheckGauges = ({ checks }: TProps) => {
  if (checks.length === 0) {
    return <p className="mt-3 text-[11px] text-foreground-muted">No quality checkers ran for this request.</p>;
  }
  return (
    <ul className="mt-3 grid grid-cols-2 gap-3">
      {checks.map((check) => {
        const score = Math.max(0, Math.min(100, check.score));
        const hasIssues = check.issues.length > 0;
        const issueLabel = hasIssues
          ? `${check.issues.length} ${check.issues.length === 1 ? 'issue' : 'issues'}`
          : 'no issues';
        return (
          <li
            key={check.name}
            className="flex flex-col items-center rounded-md border border-border bg-page px-2 py-3 text-center"
            title={hasIssues ? check.issues.join('\n') : undefined}
          >
            <svg
              role="img"
              aria-label={`${check.name}: ${score} of 100, ${issueLabel}`}
              width={SIZE}
              height={SIZE}
              viewBox={`0 0 ${SIZE} ${SIZE}`}
              className={cn(hasIssues ? 'text-danger' : 'text-accent')}
            >
              <circle
                cx={SIZE / 2}
                cy={SIZE / 2}
                r={RADIUS}
                fill="none"
                stroke="var(--color-border)"
                strokeWidth={STROKE}
              />
              <circle
                cx={SIZE / 2}
                cy={SIZE / 2}
                r={RADIUS}
                fill="none"
                stroke="currentColor"
                strokeWidth={STROKE}
                strokeLinecap="round"
                strokeDasharray={`${(score / 100) * CIRCUMFERENCE} ${CIRCUMFERENCE}`}
                transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
                className="transition-[stroke-dasharray] duration-700 motion-reduce:transition-none"
              />
              <text
                x="50%"
                y="50%"
                dominantBaseline="central"
                textAnchor="middle"
                className="fill-[var(--color-foreground)] text-xs font-semibold"
              >
                {score}
              </text>
            </svg>
            <span className="mt-2 w-full break-words text-[11px] font-medium text-foreground [overflow-wrap:anywhere]">
              {check.name}
            </span>
            <span className="mt-0.5 text-[11px] text-foreground-muted">
              {hasIssues ? '× ' : '✓ '}
              {issueLabel}
            </span>
          </li>
        );
      })}
    </ul>
  );
};
